<script setup>
// Foto do agente. É o avatar do AgentBot — o bot já tem anexo, URL e endpoint de purge,
// e uma segunda imagem para a mesma entidade acabaria divergindo com o tempo.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agent: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['updated']);

const { t } = useI18n();
const inputRef = ref(null);
const enviando = ref(false);
const urlLocal = ref(null);

const MAX_BYTES = 2 * 1024 * 1024;

const foto = computed(() => urlLocal.value || props.agent.avatar_url || '');
const iniciais = computed(() =>
  (props.agent.bot_name || props.agent.slug || '?')
    .trim()
    .slice(0, 2)
    .toUpperCase()
);

async function escolher(evento) {
  const file = evento.target.files?.[0];
  // Reset já aqui: sem isso, escolher o MESMO arquivo depois de um erro não dispara change.
  evento.target.value = '';
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    useAlert(t('POWER_AGENTS.IDENTITY.PHOTO_TYPE'));
    return;
  }
  if (file.size > MAX_BYTES) {
    useAlert(t('POWER_AGENTS.IDENTITY.PHOTO_SIZE'));
    return;
  }
  enviando.value = true;
  try {
    const bot = await powerAgentsApi.uploadAvatar(
      props.accountId,
      props.agent.agent_bot_id,
      file
    );
    // A URL do anexo demora um instante para valer; mostra a local até o refresh trazer a do servidor.
    urlLocal.value = bot?.avatar_url || URL.createObjectURL(file);
    emit('updated', bot?.avatar_url || null);
    useAlert(t('POWER_AGENTS.SAVED'));
  } catch (e) {
    useAlert(t('POWER_AGENTS.IDENTITY.PHOTO_ERROR'));
  } finally {
    enviando.value = false;
  }
}

async function remover() {
  enviando.value = true;
  try {
    await powerAgentsApi.deleteAvatar(props.accountId, props.agent.agent_bot_id);
    urlLocal.value = null;
    emit('updated', null);
    useAlert(t('POWER_AGENTS.SAVED'));
  } catch (e) {
    useAlert(t('POWER_AGENTS.IDENTITY.PHOTO_ERROR'));
  } finally {
    enviando.value = false;
  }
}
</script>

<template>
  <div class="flex items-center gap-4">
    <div
      class="relative flex items-center justify-center overflow-hidden rounded-full size-16 shrink-0 bg-n-alpha-2"
    >
      <img
        v-if="foto"
        :src="foto"
        :alt="agent.bot_name || agent.slug"
        class="object-cover size-full"
      />
      <span v-else class="text-lg font-semibold text-n-slate-11">
        {{ iniciais }}
      </span>
      <span
        v-if="enviando"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-3"
      >
        <i class="text-sm i-lucide-loader-circle animate-spin text-n-slate-11" />
      </span>
    </div>

    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-2">
        <Button
          :label="
            foto
              ? t('POWER_AGENTS.IDENTITY.PHOTO_CHANGE')
              : t('POWER_AGENTS.IDENTITY.PHOTO_ADD')
          "
          icon="i-lucide-image-up"
          slate
          faded
          sm
          :disabled="enviando || !agent.agent_bot_id"
          @click="inputRef?.click()"
        />
        <Button
          v-if="foto"
          :label="t('POWER_AGENTS.IDENTITY.PHOTO_REMOVE')"
          slate
          link
          sm
          :disabled="enviando"
          @click="remover"
        />
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.IDENTITY.PHOTO_HINT') }}
      </p>
    </div>

    <input
      ref="inputRef"
      type="file"
      accept="image/*"
      class="hidden"
      @change="escolher"
    />
  </div>
</template>
